<template>
  <div
    class="kiosk-cart-drawer"
    :class="{ 'cart-expanded': expanded }"
  >
    <div
      class="kiosk-cart-header"
      @click="$emit('toggle-expand')"
    >
      <div class="cart-header-left">
        <div
          class="cart-icon-wrapper"
          :class="{ 'cart-animating': animating }"
        >
          <KioskIcon
            name="cart"
            size="24"
            stroke-width="2"
            class="cart-icon"
          />
          <span
            v-if="totalItemCount > 0"
            class="cart-badge"
          >{{
            totalItemCount
          }}</span>
        </div>
        <div class="cart-header-text">
          <span class="cart-title">Current Order</span>
          <span class="cart-subtitle">{{
            expanded ? "Tap to close" : "Tap to view"
          }}</span>
        </div>
      </div>
      <div class="cart-header-right">
        <span class="cart-total">{{ currency }} {{ (total || 0).toFixed(2) }}</span>
        <KioskIcon
          name="chevronDown"
          size="20"
          stroke-width="2"
          class="cart-chevron"
          :class="{ rotated: expanded }"
        />
      </div>
    </div>

    <div
      v-if="expanded"
      class="kiosk-cart-content"
    >
      <div class="cart-items-list">
        <!-- Empty State -->
        <div
          v-if="items.length === 0"
          class="cart-empty-state"
        >
          <div class="cart-empty-icon">
            <KioskIcon
              name="cart"
              size="48"
              stroke-width="2"
            />
          </div>
          <p class="cart-empty-title">
            Your cart is empty
          </p>
          <p class="cart-empty-subtitle">
            Add some delicious items from the menu!
          </p>
        </div>

        <!-- Cart Items -->
        <div
          v-for="item in items"
          :key="item.id"
          class="cart-item-card"
        >
          <div class="cart-item-header">
            <div class="cart-item-info">
              <h3 class="cart-item-name">
                {{ item.name }}
              </h3>
              <div
                class="cart-item-modifiers"
                v-if="item.modifiers && item.modifiers.length > 0"
              >
                <span
                  v-for="mod in item.modifiers"
                  :key="mod.id"
                  class="cart-modifier-tag"
                >
                  {{ mod.name }}
                </span>
              </div>
            </div>
            <div class="cart-item-actions">
              <span class="cart-item-price">{{ currency }}
                {{ (item.price * item.quantity).toFixed(2) }}</span>
              <button
                class="cart-item-delete"
                @click="$emit('void-item', item)"
              >
                <KioskIcon
                  name="trash"
                  size="18"
                  stroke-width="2"
                />
              </button>
            </div>
          </div>
          <div class="cart-item-controls">
            <button
              class="cart-note-btn"
              :class="{ 'has-note': item.notes }"
              @click="$emit('show-notes', item)"
            >
              <KioskIcon
                name="note"
                size="14"
                stroke-width="2"
                class="cart-note-icon"
              />
              <span class="cart-note-text">{{
                item.notes ? "Edit Note" : "Add Note"
              }}</span>
            </button>

            <div class="cart-qty-controls">
              <button
                class="cart-qty-btn"
                @click="$emit('decrease-qty', item)"
                :disabled="item.quantity <= 1"
              >
                <KioskIcon
                  name="minus"
                  size="14"
                  stroke-width="3"
                />
              </button>
              <span class="cart-qty-value">{{ item.quantity }}</span>
              <button
                class="cart-qty-btn"
                @click="$emit('increase-qty', item)"
              >
                <KioskIcon
                  name="plus"
                  size="14"
                  stroke-width="3"
                />
              </button>
            </div>
          </div>

          <div
            v-if="item.notes"
            class="cart-item-note"
          >
            <KioskIcon
              name="note"
              size="14"
              stroke-width="2"
              class="cart-note-display-icon"
            />
            <span class="cart-note-display-text">"{{ item.notes }}"</span>
          </div>
        </div>
      </div>
      <div
        v-if="items.length > 0"
        class="cart-footer"
      >
        <div class="cart-total-row">
          <span class="cart-total-label">Subtotal</span>
          <span class="cart-total-amount">{{ currency }} {{ (subtotal != null ? subtotal : 0).toFixed(2) }}</span>
        </div>
        <div class="cart-total-row">
          <span class="cart-total-label">Tax</span>
          <span class="cart-total-amount">{{ currency }} {{ (taxTotal != null ? taxTotal : 0).toFixed(2) }}</span>
        </div>
        <div class="cart-total-row cart-total-row-final">
          <span class="cart-total-label">Total</span>
          <span class="cart-total-amount">{{ currency }} {{ (grandTotal || 0).toFixed(2) }}</span>
        </div>
        <button
          class="cart-checkout-btn"
          @click="$emit('checkout')"
          :disabled="checkoutDisabled"
        >
          <span>Checkout Now</span>
          <KioskIcon
            name="checkout"
            size="24"
            stroke-width="2"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import KioskIcon from "./KioskIcon.vue";

export default {
  name: "CartDrawerV2",
  components: {
    KioskIcon,
  },
  props: {
    expanded: Boolean,
    animating: Boolean,
    items: {
      type: Array,
      default: () => [],
    },
    totalItemCount: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 0,
    },
    subtotal: {
      type: Number,
      default: null,
    },
    taxTotal: {
      type: Number,
      default: null,
    },
    grandTotal: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: "$",
    },
    checkoutDisabled: Boolean,
  },
};
</script>

<style scoped>
.kiosk-cart-drawer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 50;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 16px 16px 0 0;
  transition: height 0.3s ease-out;
  height: 80px;
  overflow: hidden;
}

.kiosk-cart-drawer.cart-expanded {
  height: 85vh;
}

.kiosk-cart-header {
  background: #2563eb;
  color: white;
  height: 80px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s;
}

.kiosk-cart-header:hover {
  background: #1d4ed8;
}

.cart-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cart-icon-wrapper {
  position: relative;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.cart-icon-wrapper.cart-animating {
  animation: cartBump 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes cartBump {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.4) rotate(-10deg);
  }
  60% {
    transform: scale(1.4) rotate(10deg);
  }
  100% {
    transform: scale(1);
  }
}

.cart-icon {
  width: 24px;
  height: 24px;
  color: #f59e0b;
}

.cart-badge {
  position: absolute;
  top: -6px;
  right: -10px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #2563eb;
}

.cart-header-text {
  display: flex;
  flex-direction: column;
}

.cart-title {
  font-weight: bold;
  font-size: 18px;
  line-height: 1.2;
}

.cart-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.cart-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.cart-total {
  font-family: "Merriweather", serif;
  font-weight: bold;
  font-size: 24px;
}

.cart-chevron {
  width: 20px;
  height: 20px;
  transition: transform 0.3s;
}

.cart-chevron.rotated {
  transform: rotate(180deg);
}

.kiosk-cart-content {
  display: flex;
  flex-direction: column;
  height: calc(85vh - 80px);
  background: #f9fafb;
}

.cart-items-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cart-item-card {
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.cart-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.cart-item-info {
  flex: 1;
}

.cart-item-name {
  text-align: start;
  font-weight: bold;
  font-size: 18px;
  color: #111827;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.cart-item-modifiers {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.cart-modifier-tag {
  font-size: 10px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}

.cart-item-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.cart-item-price {
  font-weight: bold;
  font-size: 18px;
  color: #111827;
}

.cart-item-delete {
  padding: 6px;
  color: #9ca3af;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}

.cart-item-delete:hover {
  color: #ef4444;
  background: #fee2e2;
}

.cart-item-delete svg {
  width: 18px;
  height: 18px;
}

.cart-item-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  gap: 12px;
}

.cart-note-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: #9ca3af;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.cart-note-btn:hover {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}

.cart-note-btn.has-note {
  color: #f59e0b;
  background: #fef3c7;
  border-color: #fcd34d;
}

.cart-note-btn.has-note:hover {
  background: #fde68a;
  border-color: #fbbf24;
}

.cart-note-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.cart-note-text {
  font-size: 10px;
  font-weight: bold;
  white-space: nowrap;
}

.cart-qty-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.cart-qty-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.cart-qty-btn:hover:not(:disabled) {
  background: #f9fafb;
  transform: scale(0.95);
}

.cart-qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.cart-qty-btn svg {
  width: 14px;
  height: 14px;
}

.cart-qty-value {
  width: 32px;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  color: #111827;
}

.cart-item-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 8px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 12px;
  color: #92400e;
  font-style: italic;
}

.cart-note-display-icon {
  width: 14px;
  height: 14px;
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 2px;
}

.cart-note-display-text {
  flex: 1;
  line-height: 1.4;
}

.cart-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: #9ca3af;
  min-height: 300px;
}

.cart-empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
  color: #9ca3af;
}

.cart-empty-icon svg {
  width: 100%;
  height: 100%;
}

.cart-empty-title {
  font-weight: 500;
  font-size: 18px;
  color: #9ca3af;
  margin: 0 0 8px 0;
}

.cart-empty-subtitle {
  font-size: 14px;
  color: #d1d5db;
  margin: 0;
}

.cart-footer {
  padding: 16px;
  background: white;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
}

.cart-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 0 8px;
}

.cart-total-row-final {
  margin-top: 8px;
  margin-bottom: 16px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  font-weight: 700;
}

.cart-total-row-final .cart-total-amount {
  font-size: 1.5rem;
}

.cart-total-label {
  color: #6b7280;
  font-weight: 500;
}

.cart-total-amount {
  font-size: 24px;
  font-weight: bold;
  color: #0f172a;
}

.cart-checkout-btn {
  width: 100%;
  background: #2563eb;
  color: white;
  padding: 16px;
  border-radius: 12px;
  font-size: 20px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.cart-checkout-btn:hover:not(:disabled) {
  background: #1d4ed8;
  transform: scale(0.98);
}

.cart-checkout-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cart-checkout-btn svg {
  width: 24px;
  height: 24px;
}
</style>
